import React from 'react';

import { useSnackbar } from 'notistack';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { useUpdateOffer } from '~/hooks/apis/offers/useGetOffer';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import OfferPagePreview from '../OfferPagePreview';
import type { OfferBuilderMediaController } from '../useOfferBuilderMedia';

import ImageEditor from './ImageEditor';
import ImagesByRoom from './ImagesByRoom';
import LogoVideoSection from './LogoVideoSection';
import OfferGallery from './OfferGallery';
import type { PropertyRoomTypeWithImages } from './types';
import { setFolderInputRef } from './utils';

interface Props {
  media: OfferBuilderMediaController;
  offerId: string;
  offerSlug?: string;
  offerType?: string;
  propertyId?: string;
  propertyLogoId?: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

export default function ImagesVideo({
  media,
  offerId,
  offerSlug,
  offerType,
  propertyId,
  propertyLogoId,
  propertyRoomTypes,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const updateOfferMutation = useUpdateOffer(offerId);

  const handleFolderInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    void media.uploadFiles(files, { ignoreNonImageFiles: true });
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    void media.uploadFiles(files);
  };

  const handleSaveVideo = async (videoId: string) => {
    try {
      await updateOfferMutation.mutateAsync({
        ...(offerType ? { type: offerType } : {}),
        video_vimeo_id: videoId || null,
      });
      media.setVideoId(videoId);
      enqueueSnackbar('Offer video updated', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update offer video'), { variant: 'error' });
      throw error;
    }
  };

  return (
    <Box>
      <input
        hidden
        accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
        multiple
        type="file"
        id="offer-builder-folder-upload-input"
        ref={setFolderInputRef}
        onChange={handleFolderInputChange}
      />
      <input
        hidden
        accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
        multiple
        type="file"
        id="offer-builder-image-upload-input"
        onChange={handleFileInputChange}
      />

      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4">Images & Video</Typography>
        <Typography variant="body1" color="text.secondary">
          Manage offer media from one place. Upload property images, organise them by room type, choose the property
          logo, and attach video content.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Name folders or files after a room to add images to that room, for example{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Deluxe Suite/hero.jpg
          </Box>{' '}
          or{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Deluxe Suite balcony.jpg
          </Box>
          . Anything without a room match is added to the offer gallery.
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 4 }}>
        <Stack direction="row" spacing={1.5}>
          <Button
            component="label"
            variant="outlined"
            htmlFor="offer-builder-folder-upload-input"
            disabled={media.isUploadingImages}
            startIcon={media.isUploadingImages ? <CircularProgress size={18} /> : <DriveFolderUploadOutlinedIcon />}
          >
            Upload folder
          </Button>
          <Button
            component="label"
            variant="contained"
            htmlFor="offer-builder-image-upload-input"
            disabled={media.isUploadingImages}
            startIcon={
              media.isUploadingImages ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <AddPhotoAlternateOutlinedIcon />
              )
            }
          >
            Add image
          </Button>
        </Stack>
        <OfferPagePreview offerId={offerId} offerSlug={offerSlug} />
      </Stack>

      <OfferGallery
        hiddenImagePublicIds={media.hiddenImagePublicIds}
        mediaImages={media.mediaImages}
        imageEdits={media.imageEdits}
        imagePublicIdReplacements={media.imagePublicIdReplacements}
        isUploadDisabled={media.isUploadingImages}
        onDeleteImage={media.deleteImage}
        onReorderImages={media.reorderOfferGalleryImages}
        onSaveImageEdit={media.saveImageEdit}
        onToggleHiddenImage={media.toggleHiddenImage}
        onUploadImages={media.uploadFilesToOfferGallery}
      />

      <ImagesByRoom
        excludedImagePublicIds={media.deletedImagePublicIds}
        hiddenImagePublicIds={media.hiddenImagePublicIds}
        imagePublicIdReplacements={media.imagePublicIdReplacements}
        mediaImages={media.mediaImages}
        onEditImage={media.editRoomImage}
        onRemoveImageFromRoom={media.removeImageFromRoom}
        onReorderRoomImages={media.reorderRoomImages}
        onToggleHiddenImage={media.toggleHiddenImage}
        onUploadImagesToRoom={media.uploadFilesToRoom}
        propertyRoomTypes={propertyRoomTypes}
        roomImageOrderOverrides={media.roomImageOrderOverrides}
        uploadedRoomAssignments={media.uploadedRoomAssignments}
      />
      <LogoVideoSection
        isSavingVideo={updateOfferMutation.isPending}
        propertyId={propertyId}
        propertyLogoId={propertyLogoId}
        videoId={media.videoId}
        onSaveVideo={handleSaveVideo}
      />
      <ImageEditor
        edit={media.roomImagePendingEdit ? media.imageEdits[media.roomImagePendingEdit.publicId] : undefined}
        image={media.roomImagePendingEdit}
        open={!!media.roomImagePendingEdit}
        onClose={() => media.setRoomImagePendingEdit(undefined)}
        onDeleteImage={media.removeRoomImageFromEditor}
        onSave={media.saveImageEdit}
      />
      <DeleteConfirmationDialog
        body="This will remove the image from the room. This action cannot be undone."
        open={!!media.roomImagePendingDeletion}
        title="Delete image from room?"
        onClose={media.closeRoomImageDeleteDialog}
        onConfirm={media.confirmRemoveRoomImage}
      />
    </Box>
  );
}
